import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { get } from 'lodash'
import HttpRequest from '@/config/axios.config'
import ExportLoadingIndicator from './ExportLoadingIndicator'
import { downloadFromUrl, saveOrderExcel } from '@/utils/filesaver'

type ExportStatus = 'loading' | 'success' | 'error'

const SUCCESS_AUTO_CLOSE_MS = 2000

function ExportDataLandingPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<ExportStatus>('loading')
  const [message, setMessage] = useState('')
  const name = searchParams.get('name')
  const flow = searchParams.get('flow')

  useEffect(() => {
    const sendErrorToOpener = (msg: string) => {
      if (window.opener) {
        window.opener.postMessage(
          { type: 'EXPORT_ERROR', message: msg },
          window.location.origin
        )
      }
    }

    const triggerExport = async () => {
      if (!name || !flow) {
        setStatus('error')
        return
      }

      const payload = Object.fromEntries(searchParams.entries())
      const isCAP = name === 'inspections_export_supplier_cap'

      try {
        const response = await HttpRequest.post(
          '/export-data',
          payload,
          isCAP ? { responseType: 'arraybuffer' } : {}
        )

        if (isCAP) {
          const contentDisposition =
            response.headers?.['content-disposition'] || ''
          let fileName = 'Supplier-CAP'
          const matches = /filename="?([^";]+)"?/.exec(contentDisposition)
          if (matches?.[1]) {
            fileName = matches[1].replace(/\.xlsx$/, '')
          }
          saveOrderExcel(new Blob([response.data]), fileName)
          setStatus('success')
          return
        }

        const downloadUrl = get(response, 'data.data')
        if (downloadUrl) {
          downloadFromUrl(downloadUrl)
          setStatus('success')
          return
        }

        const msg = get(response, 'data.message')
        if (msg) sendErrorToOpener(msg)
        setStatus('error')
        setMessage(msg || 'Export failed. Please try again.')
      } catch (err: any) {
        const errorMessage =
          get(err, 'response.data.message') || get(err, 'message')
        if (errorMessage) sendErrorToOpener(errorMessage)
        setStatus('error')
        setMessage(errorMessage || 'Export failed. Please try again.')
      }
    }

    triggerExport()
  }, [name, flow])

  useEffect(() => {
    if (status !== 'success') return
    const timer = setTimeout(() => {
      window.close()
    }, SUCCESS_AUTO_CLOSE_MS)
    return () => clearTimeout(timer)
  }, [status])

  if (status === 'success') {
    return (
      <ExportLoadingIndicator
        loop={false}
        title={
          <>
            Download completed. <br />
            This tab will close automatically.
          </>
        }
      />
    )
  }

  if (status === 'error') {
    return (
      <ExportLoadingIndicator
        loop={false}
        title={
          <>
            {message || 'Export failed. Please try again.'} <br />
            You may close this tab.
          </>
        }
      />
    )
  }

  return <ExportLoadingIndicator />
}

export default ExportDataLandingPage
